import {
  Badge,
  Flex,
  FlexProps,
  Icon,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useBoolean,
} from '@chakra-ui/react'
import React from 'react'
import IconCallStart from '~/assets/svgs/icon-call-start.svg?react'
import IconDownChevron from '~/assets/svgs/icon-down-chevron.svg?react'
import { useAnalytics } from '~/hooks/useAnalytics'
import { ContactNumber } from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'
import { ZIndex } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'

import {
  CallContactButtonMenuVariants,
  CallContactButtonVariants,
  useCallContactButtonMenuStyles,
  useCallContactButtonStyles,
} from './componentStyles/CallContactButton.style'

interface CallContactButtonMenuProps {
  contactNumbers: ContactNumber[]
  onStartCall: (number: ContactNumber) => void
}
const CallContactButtonMenu = ({
  contactNumbers,
  onStartCall,
}: CallContactButtonMenuProps) => {
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const handleStartCall = (number: ContactNumber) => {
    onStartCall(number)
    track(WebPhoneAnalyticsEvents.SearchDropdownItemClicked)
  }

  const variant =
    contactNumbers.length > 5
      ? CallContactButtonMenuVariants.Scrollable
      : CallContactButtonMenuVariants.Fixed

  const styles = useCallContactButtonMenuStyles({ variant })
  return (
    <Flex sx={styles.container}>
      {contactNumbers.map((contact) => {
        return (
          <Flex
            key={contact.id}
            onClick={() => handleStartCall(contact)}
            sx={styles.itemContainer}
          >
            <Text sx={styles.itemText}>{`Call ${formatPhoneNumber(
              contact.number,
            )} - ${toNounCasing(contact.label)}`}</Text>
            {contact.isPrimary && <Badge sx={styles.itemBadge}>Primary</Badge>}
          </Flex>
        )
      })}
    </Flex>
  )
}

interface CallContactButtonProps extends FlexProps {
  defaultContactNumber: ContactNumber
  otherContactNumbers?: ContactNumber[]
  onStartCall: (number: ContactNumber) => void
  isRecentDialed?: boolean
  isHidden?: boolean
}
export const CallContactButton = ({
  defaultContactNumber,
  otherContactNumbers,
  onStartCall,
  isRecentDialed,
  isHidden,
  ...rest
}: CallContactButtonProps) => {
  const [menuOpen, setMenuOpen] = useBoolean(false)

  if (isHidden && menuOpen) {
    setMenuOpen.off()
  }
  const handleMenuButtonClick = () => {
    setMenuOpen.toggle()
  }

  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const handlePrimaryStartCall = (number: ContactNumber) => {
    onStartCall(number)
    if (isRecentDialed) {
      track(WebPhoneAnalyticsEvents.RecentlyDialedStartCallClicked)
    } else if (otherContactNumbers && otherContactNumbers.length > 1) {
      track(WebPhoneAnalyticsEvents.SearchStartCallWithOptionsClicked)
    } else {
      track(WebPhoneAnalyticsEvents.SearchStartCallClicked)
    }
  }

  const variant = isHidden
    ? CallContactButtonVariants.Hidden
    : CallContactButtonVariants.Visible
  const styles = useCallContactButtonStyles({ variant })
  return (
    <Popover isOpen={menuOpen}>
      <Flex className="contact-button" sx={styles.container} {...rest}>
        <PopoverAnchor>
          <Flex
            as="button"
            onClick={() => handlePrimaryStartCall(defaultContactNumber)}
            sx={styles.callButton}
          >
            <Icon as={IconCallStart} sx={styles.callButtonIcon} />
            <Text sx={styles.callButtonText}>Start Call</Text>
          </Flex>
        </PopoverAnchor>
        {otherContactNumbers && otherContactNumbers.length > 1 && (
          <Flex
            as="button"
            sx={styles.menuButton}
            onClick={handleMenuButtonClick}
          >
            <PopoverTrigger>
              <Flex sx={styles.menuButtonIconContainer}>
                <Icon as={IconDownChevron} sx={styles.menuButtonIcon} />
              </Flex>
            </PopoverTrigger>
            <Flex zIndex={ZIndex.popover}>
              <Portal>
                <PopoverContent width="fit-content" bg="transparent">
                  <PopoverBody minWidth="fit-content" padding="0">
                    <CallContactButtonMenu
                      contactNumbers={otherContactNumbers}
                      onStartCall={onStartCall}
                    />
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Flex>
          </Flex>
        )}
      </Flex>
    </Popover>
  )
}
